import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Table,
  Alert,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import FileAuditUploadService from "../../../services/fileAuditUpload.service";
import { FileAudit } from "../../../models/FileAudit";
import "./style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";

// --- INICIO DE LA SOLUCIÓN ---
// 1. Se añade 'onHandleReloadFiles' para la sincronización
interface FileUploadProps {
  auditId: number;
  onUploadSuccess: () => void;
  isNeededReloadFiles: boolean;
  onHandleReloadFiles: () => void;
}
// --- FIN DE LA SOLUCIÓN ---

const FileUpload = ({
  auditId,
  onUploadSuccess,
  isNeededReloadFiles,
  onHandleReloadFiles, // Se recibe la prop
}: FileUploadProps) => {
  const [files, setFiles] = useState<FileAudit[]>([]);
  const [filesAnswer, setFilesAnswer] = useState<FileAudit[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleUpload(droppedFiles);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [auditId, isNeededReloadFiles]);

  const loadFiles = async () => {
    setError("");
    try {
      const data = await FileAuditUploadService.getFiles(auditId);
      const filesAudit = data.filter(
        (fileAudit) => fileAudit.auditQuestionId === null
      );
      setFiles(filesAudit);

      const filesAns = data.filter(
        (file) => file.auditId !== null && file.auditQuestionId !== null
      );
      setFilesAnswer(filesAns);
    } catch (err: any) {
      setError(err.message || "Error al cargar archivos");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  const handleUpload = async (filesToUpload: FileList) => {
    if (!filesToUpload || filesToUpload.length === 0) return;

    setLoading(true);
    setError("");

    try {
      const uploadedFiles = await FileAuditUploadService.uploadFiles(
        auditId,
        filesToUpload
      );
      setFiles((prev) => [...uploadedFiles, ...prev]);
      onUploadSuccess();
    } catch (err: any) {
      setError(err.message || "Error al subir archivos");
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (fileId: number) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este archivo?")) {
      return;
    }

    setDeletingId(fileId);
    setError("");
    try {
      await FileAuditUploadService.deleteFile(fileId);
      await loadFiles();
      // --- INICIO DE LA SOLUCIÓN ---
      // 2. Se notifica al padre que un archivo ha sido eliminado
      onHandleReloadFiles();
      // --- FIN DE LA SOLUCIÓN ---
    } catch (err) {
      setError("Error al eliminar el archivo");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="file-upload-container">
      <style>{`
        .drop-zone {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          color: #aaa;
          transition: all 0.3s ease;
        }
        .drop-zone.dragging {
          border-color: #0d6efd;
          background-color: #e7f1ff;
          color: #0d6efd;
        }
        .drop-zone-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
      `}</style>

      <div
        className={`drop-zone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="drop-zone-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 0a5.53 5.53 0 0 0-3.594 1.342c-.766.66-1.321 1.52-1.464 2.383C1.266 4.095 0 5.555 0 7.318 0 9.366 1.708 11 3.781 11H7.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V11h4.188C14.502 11 16 9.57 16 7.773c0-1.636-1.242-2.969-2.834-3.194C12.923 1.99 10.69 0 8 0zm-.5 14.5V11h1v3.5a.5.5 0 0 1-1 0z"
            />
          </svg>
        </div>
        <h5>Arrastra y suelta archivos aquí</h5>
        <p className="text-muted">o</p>
        <Form.Group
          controlId="formFileGeneralAudit"
          className="mb-3 d-flex justify-content-center"
        >
          <Form.Control
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={loading}
            ref={fileInputRef}
            className="d-none"
          />
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faUpload} />
            <span className="ms-2">
              {loading ? "Subiendo..." : "Seleccionar archivos"}
            </span>
          </Button>
        </Form.Group>
      </div>

      {error && (
        <Alert
          variant="danger"
          className="mt-3"
          onClose={() => setError("")}
          dismissible
        >
          {error}
        </Alert>
      )}

      <div className="mt-4">
        <h5 className="text-center">Archivos generales de la auditoría</h5>
        <Table striped bordered hover responsive>
          <thead className="table-secondary">
            <tr>
              <th className="text-center">Nombre del Archivo</th>
              <th className="text-center">Fecha de Subida</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => (
              <tr key={file.id}>
                <td>{file.originalFileName}</td>
                <td className="text-center">
                  {new Date(file.uploadDate).toLocaleDateString()}
                </td>
                <td className="text-center">
                  <Button
                    variant="primary"
                    href={file.shareableUrl}
                    target="_blank"
                    size="sm"
                    title="Ver archivo"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                  <Button
                    variant="danger"
                    className="ms-2"
                    size="sm"
                    title="Eliminar archivo"
                    onClick={() => handleDelete(file.id)}
                    disabled={deletingId === file.id}
                  >
                    {deletingId === file.id ? (
                      <Spinner as="span" animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} />
                    )}
                  </Button>
                </td>
              </tr>
            ))}
            {files.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted">
                  No hay archivos adjuntos
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div className="mt-4">
        <h5 className="text-center">Archivos adjuntos en respuestas</h5>
        <Table striped bordered hover responsive>
          <thead className="table-secondary">
            <tr>
              <th className="text-center">Pregunta</th>
              <th className="text-center">Nombre del Archivo</th>
              <th className="text-center">Fecha de Subida</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filesAnswer.map((file) => (
              <tr key={file.id}>
                <td>{file.auditQuestion?.questionDesc}</td>
                <td>{file.originalFileName}</td>
                <td className="text-center">
                  {new Date(file.uploadDate).toLocaleDateString()}
                </td>
                <td className="text-center">
                  <Button
                    variant="primary"
                    href={file.shareableUrl}
                    target="_blank"
                    size="sm"
                    title="Ver archivo"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                  <Button
                    variant="danger"
                    className="ms-2"
                    size="sm"
                    title="Eliminar archivo"
                    onClick={() => handleDelete(file.id)}
                    disabled={deletingId === file.id}
                  >
                    {deletingId === file.id ? (
                      <Spinner as="span" animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} />
                    )}
                  </Button>
                </td>
              </tr>
            ))}
            {filesAnswer.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  No hay archivos adjuntos en las preguntas
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default FileUpload;
